import { useTranslation } from 'react-i18next';
import type { UspacyLegalDocumentLink, UspacyLegalDocuments } from '@aica/api-types';
import { Checkbox } from '@/components/ui/checkbox';
import { cn, resolveLocale } from '@/lib/utils';

export interface UspacyLegalActionsState {
  terms: boolean;
  privacy: boolean;
  processingInstruction: boolean;
  authority: boolean;
}

export const EMPTY_USPACY_LEGAL_ACTIONS: UspacyLegalActionsState = {
  terms: false,
  privacy: false,
  processingInstruction: false,
  authority: false,
};

interface UspacyLegalActionsProps {
  documents: UspacyLegalDocuments;
  actions: UspacyLegalActionsState;
  onActionChange: (action: keyof UspacyLegalActionsState, checked: boolean) => void;
  className?: string;
}

/**
 * Does the reader need telling that the linked documents are the governing
 * ENGLISH text?
 *
 * `isLanguageFallback` answers a different question: Uspacy documents four
 * placement locales (uk/en/es/de), so it is computed from the locale the
 * PLACEMENT routed with. A Russian-speaking admin folds to 'en' there and the
 * flag folds to false with them — hiding the notice from exactly the person who
 * cannot read the document, while a Ukrainian or Spanish one is told. The
 * notice is about the language the person is reading.
 *
 * Exported so both entry points (the link page and the reauthorization card)
 * are provably the same decision — it lived inline in ConsentSection, which is
 * how the link page kept the raw flag.
 */
export function shouldDiscloseGoverningLanguage(
  documents: UspacyLegalDocuments,
  uiLanguage: string
): boolean {
  const language = (uiLanguage.split('-')[0] ?? 'en').toLowerCase();
  return language !== 'en' || documents.terms.isLanguageFallback === true;
}

/**
 * One UI boundary for the four legally distinct Uspacy activation actions.
 * Link and reauthorization flows must not drift into different text, document
 * links or preselection behaviour.
 */
export function UspacyLegalActions({
  documents,
  actions,
  onActionChange,
  className,
}: UspacyLegalActionsProps) {
  const { t } = useTranslation('settings');
  const controls: Array<{
    action: keyof UspacyLegalActionsState;
    label: string;
    linkLabel?: string;
    document?: UspacyLegalDocumentLink;
  }> = [
    {
      action: 'terms',
      label: t('uspacyLink.termsLabel'),
      linkLabel: t('uspacyLink.termsLink'),
      document: documents.terms,
    },
    {
      action: 'privacy',
      label: t('uspacyLink.privacyLabel'),
      linkLabel: t('uspacyLink.privacyLink'),
      document: documents.privacy,
    },
    {
      action: 'processingInstruction',
      label: t('uspacyLink.instructionLabel'),
      linkLabel: t('uspacyLink.instructionLink'),
      document: documents.processingInstruction,
    },
    {
      action: 'authority',
      label: t('uspacyLink.authorityLabel'),
    },
  ];

  return (
    <div className={cn('space-y-3', className)}>
      {shouldDiscloseGoverningLanguage(documents, resolveLocale()) ? (
        <p className="text-xs text-fg-muted" data-testid="uspacy-language-fallback">
          {t('uspacyLink.languageFallback')}
        </p>
      ) : null}
      <div className="space-y-3 rounded-card border border-border p-3">
        {controls.map(({ action, label, linkLabel, document }) => (
          <div className="flex items-start gap-3" key={action}>
            <Checkbox
              checked={actions[action]}
              onCheckedChange={(value) => onActionChange(action, value === true)}
              aria-label={label}
            />
            <div>
              <p className="text-sm text-fg">{label}</p>
              {linkLabel && document ? (
                <a
                  className="mt-1 inline-block text-xs text-accent underline"
                  href={document.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {linkLabel} ({document.version})
                </a>
              ) : null}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
